/**
 * Campus life, assembled from the pages the institute publishes under
 * bietdvg.edu/campus_life/… and bietdvg.edu/facilities/… (fetched 2026-08-07).
 *
 * The old site has no /campus_life index — it 404s. What exists is a scatter of
 * leaf pages reachable only from the nav: the Student Academic Council, three
 * service wings, the green-campus initiative, four innovation cells, and a dozen
 * facility pages. This file gathers them so /campus-life can be the hub the
 * source never had.
 *
 * Everything here is the institute's own claim. Figures, names and designations
 * are reproduced as published; only section framing and light copy-editing
 * (sentence case, spelling of "Davangere"/"Bangalore" left alone) are ours.
 * Where a source page carries nothing but boilerplate Lorem ipsum — canteen and
 * language lab both do — the Lorem is dropped and the real paragraph beneath it
 * kept.
 *
 * Two things deliberately NOT reproduced: the hostel bank account and IFSC the
 * source publishes, and the five SAC gallery images whose URLs 404.
 *
 * The Davana dates and event categories come from the festival poster inside the
 * institute's own SAC collage image — the SAC page's prose names the festival but
 * gives neither.
 */
import type { Media } from "@zivin-labs/site-content";
import {
  SAC_PHOTOS,
  GYM_PHOTOS,
  AUDITORIUM_PHOTOS,
  SPORTS_PHOTOS,
  NCC_PHOTOS,
  NSS_PHOTOS,
  RED_CROSS_PHOTOS,
  HEALTH_PHOTOS,
  CANTEEN_PHOTOS,
  WATER_PHOTOS,
  WASTE_PHOTOS,
  GREEN_CAMPUS_PHOTOS,
  NAIN_PHOTOS,
  EDC_PHOTOS,
  SKILL_CENTRE_PHOTOS,
  GUEST_HOUSE_PHOTOS,
  OFFICER_PORTRAITS,
  LIBRARY_PHOTOS,
} from "./campusLifeMedia";
import { HOSTELS } from "./hostels";
import { STAFF } from "./staff";
import { ROUTES } from "./routes";

/** Pick one ingested photo out of a pool by its key's last segment. */
function shot(pool: Media[], slug: string): Media {
  const found = pool.find((m) => m.url.endsWith(`/${slug}`));
  if (!found) throw new Error(`campusLife: no ingested photo "${slug}"`);
  return found;
}

/** A named person as the source designates them. */
export interface Person {
  name: string;
  role: string;
  /**
   * Slug in the staff directory to borrow a portrait from — the same device
   * library.ts uses. Set only where the staff record's name matches this one
   * exactly (allowing for reordered initials), because a near-match would put
   * the wrong face against a real person's name. The student member is not
   * staff and has no portrait, and neither is Dr. G P Desai, whose record left
   * STAFF when he retired (relieved 31-07-2025) — his slug pointed at the
   * deleted record and resolved to nothing, so it has been dropped.
   */
  staffSlug?: string;
}

/** Portrait for a council/office member, where one was matched. */
export function personPhoto(p: Person): Media | undefined {
  if (!p.staffSlug) return undefined;
  return STAFF.find((m) => m.slug === p.staffSlug)?.photo;
}

// ---------------------------------------------------------------------------
// Student Academic Council — the spine of student life since 1984
// ---------------------------------------------------------------------------

export const SAC = {
  name: "Students' Academic Council",
  since: 1984,
  intro: [
    "In the present day of high competition one has to have a very good communication and soft skills along with curriculum inputs. BIET believes in overall development of its students who can adopt in any kind of environment and succeed. Keeping this in view the Students' Academic Council has been formed in the institute and has been very active since 1984.",
    "Activities of the council are totally managed by the students. All the students of the institute elect their class representatives, who in turn elect the executive council. Four staff conveners guide the students in conducting various activities.",
  ],
  /** What the council actually runs, in the source's own order. */
  activities: [
    {
      title: "Ethnic Day",
      detail: "Held every year; students exhibit the cultures of different parts of the country.",
    },
    {
      title: "Fashion show",
      detail: "An opportunity for students to exhibit their creative talents.",
    },
    {
      title: "Paper presentation",
      detail: "A competition that helps students improve communication and demonstration skills.",
    },
    {
      title: "Sports",
      detail:
        "Regular sports activities, giving students ample opportunity to improve sporting talent.",
    },
    {
      title: "The multi gym",
      detail: "Managed by the students' council itself.",
    },
  ],
  outward:
    "The council also encourages students to take part in activities conducted by Visvesvaraya Technological University and by other institutes across the country.",
  office: {
    president: [
      { staffSlug: "y-vrushabhendrappa", name: "Prof. Y Vrushabhendrappa", role: "Director" },
      { staffSlug: "h-b-aravind", name: "Dr. H B Aravind", role: "Principal" },
    ] as Person[],
    dean: [{ staffSlug: "vinutha-h-p", name: "Dr. Vinutha H P", role: "Professor" }] as Person[],
    advisors: [
      { staffSlug: "m-c-patel", name: "Sri M. C. Patel", role: "Senior Faculty" },
      { staffSlug: "k-c-devendrappa", name: "Dr. K C Devendrappa", role: "Professor" },
      { staffSlug: "g-manavendra", name: "Dr. G Manavendra", role: "Professor" },
      { name: "Dr. G P Desai", role: "Professor" },
    ] as Person[],
    conveners: [
      { staffSlug: "c-m-kalleshappa", name: "Dr. Kalleshappa C M", role: "Associate Professor" },
      { staffSlug: "vinutha-h-p", name: "Dr. Vinutha H P", role: "Professor" },
    ] as Person[],
    advisory: [{ name: "Aditi N. Kashyap", role: "Student (E&CE)" }] as Person[],
  },
  photos: SAC_PHOTOS,
  source: "https://www.bietdvg.edu/campus_life/sac",
};

// ---------------------------------------------------------------------------
// Davana — the council's annual cultural festival
//
// The SAC page names the festival in one clause and gives nothing else. Every
// detail below — dates, venue, the six disciplines and their events, and the
// organising office — is read off the Davana '24 poster inside the institute's
// own SAC collage image (sites/biet/campus-life/sac/collage1.jpg), which is the
// only place it publishes them. Reproduced as the poster sets them, including
// its own spellings ("Anthyakshari", "Mad-Ads"). The poster also credits four
// organisers under "SWC" titles that disagree with the SAC roster above; they
// are not carried here.
// ---------------------------------------------------------------------------

export interface DavanaDiscipline {
  name: string;
  events: string[];
}

export const DAVANA = {
  name: "Davana",
  edition: "Davana '24",
  blurb:
    "The council's annual mega cultural festival — a very big cultural bonanza which creates a platform for exhibiting various cultural talents.",
  dates: "31 May & 1 June 2024",
  venue: "Cricket Ground, BIET",
  disciplines: [
    { name: "Music", events: ["Light vocal / classical", "Western vocal solo", "Group song Indian", "Anthyakshari Kannada"] },
    { name: "Dance", events: ["Classical solo", "Group dance", "Face off (desi-beats)"] },
    { name: "Arts", events: ["Rangoli", "Collage"] },
    { name: "Theatre", events: ["Mad-ads", "Mime", "Skit"] },
    { name: "Cinematography", events: ["Short movie making", "Photography"] },
    { name: "Literary", events: ["Quiz", "Debate"] },
  ] as DavanaDiscipline[],
  photo: shot(SAC_PHOTOS, "collage1.jpg"),
  source: "https://www.bietdvg.edu/campus_life/sac",
};

// ---------------------------------------------------------------------------
// Service wings — NSS, NCC, Youth Red Cross
// ---------------------------------------------------------------------------

export interface ServiceWing {
  slug: string;
  abbr: string;
  name: string;
  motto?: string;
  /** One line for the card — ours, not the source's. */
  strap: string;
  /** The wing's programme officer, as the source names them. */
  officer: Person;
  since?: string;
  vision?: string;
  mission: string[];
  /** Further paragraphs the source publishes — history, emblem, objectives. */
  notes?: Array<{ title: string; body: string[] }>;
  /**
   * Card/hero accent. A design choice, not a claim — except for NSS, whose page
   * states the emblem's "Red & Blue colors" and glosses both.
   */
  accent: string;
  /**
   * The single frame the card and detail hero lead with — chosen, not photos[0].
   * The NSS page publishes nothing but a 155px headshot of its officer, so NSS
   * borrows the blood-camp frame from the Red Cross page: the banner in that
   * photograph reads "National Service Scheme", so it is an NSS camp that the
   * source happens to file elsewhere.
   */
  cardPhoto: Media;
  /** Thumbnail of the programme officer, shown beside their name. */
  officerPhoto?: Media;
  photos: Media[];
  source: string;
}

export const SERVICE_WINGS: ServiceWing[] = [
  {
    slug: "nss",
    abbr: "NSS",
    name: "National Service Scheme",
    motto: "Not Me But You",
    strap:
      "The community-service wing — needs of the community identified, and students put to solving them.",
    accent: "#1b3f8b",
    cardPhoto: shot(RED_CROSS_PHOTOS, "red-cross-01.jpg"),
    officerPhoto: OFFICER_PORTRAITS["nss"],
    officer: {
      name: "T. K. Krishna Kumar",
      role: "Asst. Prof. in Physics · NSS Programme Officer",
    },
    vision:
      "To understand the community in which they work and understand themselves in relation to their community; to develop among themselves a sense of social and civic responsibility; to practice national integration and social harmony.",
    mission: [
      "To identify the needs and problems of the community and involve them in problem-solving.",
      "To utilise their knowledge in finding practical solutions to individual and community problems.",
      "To develop the competence required for group living and the sharing of responsibilities.",
      "To gain skills in mobilising community participation, acquire leadership qualities and democratic attitudes.",
      "To develop the capacity to meet emergencies and natural disasters.",
    ],
    notes: [
      {
        title: "The motto",
        body: [
          "“Not Me But You” reflects the essence of democratic living and upholds the need for selfless service. The philosophy of the NSS underlines the belief that the welfare of an individual is ultimately dependent on the welfare of society as a whole, and therefore NSS volunteers shall strive for the well-being of society.",
        ],
      },
      {
        title: "The emblem",
        body: [
          "The logo is based on the giant Rath Wheel of the world-famous Konark Sun Temple in Odisha. The wheel portrays the cycle of creation, preservation and release, and signifies movement across time and space — continuity as well as change.",
          "The eight bars in the wheel represent the 24 hours of a day. Red indicates that the volunteer is lively, active and full of high spirit; navy blue indicates the cosmos, of which the NSS is a tiny part.",
        ],
      },
      {
        title: "Origin",
        body: [
          "On 24 September 1969 the then Union Education Minister, Dr. V.K.R.V. Rao, launched the NSS programme in 37 universities covering all states. It began in the Gandhi Centenary Year, as it was Gandhiji who inspired Indian youth to take part in the movement for independence and the social upliftment of the nation.",
        ],
      },
    ],
    photos: NSS_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/nss",
  },
  {
    slug: "ncc",
    abbr: "NCC",
    name: "National Cadet Corps",
    motto: "Unity and Discipline",
    strap:
      "Character, comradeship and discipline — and a route into a career with the Armed Forces.",
    accent: "#16457e",
    cardPhoto: shot(NCC_PHOTOS, "ncc-05.jpg"),
    officerPhoto: OFFICER_PORTRAITS["ncc"],
    officer: { name: "Lt. Anjaneya L.H.", role: "Associate NCC Officer" },
    vision:
      "To develop a committed reservoir of cadets who are resourceful, responsible, resilient and loyal leaders, able to face the challenges of a changing world and to play a positive role in inculcating the spirit of selfless service and the dignity of labour among the student community.",
    mission: [
      "To develop character, comradeship, discipline, a secular outlook, the spirit of adventure and the ideals of selfless service amongst the youth of the country.",
      "To create a human resource of organised, trained and motivated youth, to provide leadership in all walks of life and always available for the service of the nation.",
      "To provide a suitable environment to motivate the youth to take up a career in the Armed Forces.",
    ],
    photos: NCC_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/ncc",
  },
  {
    slug: "red-cross",
    abbr: "YRCW",
    name: "Youth Red Cross Wing",
    strap:
      "Humanitarian work for students aged 18 to 30, under the district branch of the Indian Red Cross Society.",
    accent: "#c8102e",
    cardPhoto: shot(RED_CROSS_PHOTOS, "red-cross-03.jpg"),
    officerPhoto: OFFICER_PORTRAITS["red-cross"],
    officer: {
      name: "Dr. S. M. Chandrasekhara",
      role: "Asst. Prof. in Textiles · YRCW Programme Officer",
    },
    since: "2006-07",
    mission: [
      "Taking care of their health and that of others.",
      "Understanding and accepting civil responsibilities.",
      "Maintaining the spirit of friendliness and helpfulness towards other children in India and all over the world.",
      "To inculcate in youth a service motto towards serving the less privileged.",
      "Dissemination of the Red Cross Movement.",
    ],
    notes: [
      {
        title: "At BIET",
        body: [
          "The Youth Red Cross Wing was started in the academic year 2006-07 and is part of the district branch of the Indian Red Cross Society. Students in colleges aged between 18 and 30 years are members of the Youth Red Cross.",
        ],
      },
      {
        title: "The parent society",
        body: [
          "The Indian Red Cross is a voluntary humanitarian organisation with a network of over 700 branches throughout the country, providing relief in times of disaster and promoting the health and care of vulnerable people. It is a leading member of the International Red Cross and Red Crescent Movement.",
          "The Indian Red Cross Society was established in 1920 under the Indian Red Cross Society Act, and has 35 state and union-territory branches. The President of India is its President and the Union Health Minister its Chairman.",
        ],
      },
    ],
    photos: RED_CROSS_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/redcross",
  },
];

/** Look a wing up by its slug, for /campus-life/service/:slug. */
export function getServiceWing(slug: string): ServiceWing | undefined {
  return SERVICE_WINGS.find((w) => w.slug === slug);
}

/** The seven fundamental principles, published on the Red Cross page. */
export const RED_CROSS_PRINCIPLES: Array<{ name: string; gloss: string }> = [
  { name: "Humanity", gloss: "To prevent and alleviate human suffering wherever it may be found." },
  { name: "Impartiality", gloss: "No discrimination as to nationality, race, belief, class or politics." },
  { name: "Neutrality", gloss: "The Movement may not take sides in hostilities or controversies." },
  { name: "Independence", gloss: "National societies must always maintain their autonomy." },
  { name: "Voluntary service", gloss: "Not prompted in any manner by desire for gain." },
  { name: "Unity", gloss: "Only one society in any one country, open to all." },
  { name: "Universality", gloss: "All societies have equal status, responsibilities and duties." },
];

// ---------------------------------------------------------------------------
// BIGc — BIET Initiatives for Green campus
//
// Sourced and kept here, but NOT rendered on /campus-life: the green-campus
// story belongs with /sustainability, which /campus-life links to in its side
// nav. Wire this in there rather than re-adding a section to the campus-life hub.
// ---------------------------------------------------------------------------

export const GREEN_CAMPUS = {
  name: "BIET Initiatives for Green campus",
  abbr: "BIGc",
  strap: "where human and nature go hand in hand",
  coordinator: {
    name: "Dr. Veena Kumara Adi",
    role: "Associate Professor, Dept. of Bio Technology",
  } as Person,
  chairman: { staffSlug: "h-b-aravind", name: "Dr. H B Aravind", role: "Principal" } as Person,
  intro: [
    "BIET, being a learning centre, is a self-contained community that supports academic programmes enduring an environmental ethic. Natural-resource conservation and the environmental humanities can empower young people to innovate and implement eco-friendly strategies, and improvements in pollution prevention serve as crucial lessons in environmental accountability.",
    "BIGc is a long-term commitment of campus-wide environmental retooling, for the benefit of environmental compliance and improved health and safety. Team BIGc facilitates and coordinates initiatives by promoting heightened awareness of environmentally sound practices in every arena of science and engineering.",
  ],
  /** Departments that file their own annual BIGc report. */
  reportingDepartments: [
    "Civil Engineering",
    "Mechanical Engineering",
    "Electrical & Electronics Engineering",
    "Electronics & Communication Engineering",
    "Computer Science and Engineering",
    "Information Science and Engineering",
    "Bio Technology",
    "Mathematics / Physics / Chemistry",
    "Textile Technology",
    "Artificial Intelligence and Machine Learning",
    "Computer Science and Engineering (Data Science)",
    "Chemical Engineering",
  ],
  photos: GREEN_CAMPUS_PHOTOS,
  source: "https://www.bietdvg.edu/campus_life/green_campus",
};

// ---------------------------------------------------------------------------
// Innovation and enterprise cells
// ---------------------------------------------------------------------------

export interface Cell {
  slug: string;
  name: string;
  abbr?: string;
  /**
   * The card's whole description — a summary of `body`, not a teaser for it.
   * `body` carries the institute's fuller prose and is kept for a detail page.
   */
  strap: string;
  body: string[];
  /** Compact footer line on the card: who runs it, or where it is. */
  meta?: string;
  facts?: Array<{ label: string; value: string }>;
  people?: Person[];
  photos?: Media[];
  source: string;
}

export const CELLS: Cell[] = [
  {
    slug: "nain",
    name: "New Age Incubation Network",
    abbr: "NAIN",
    strap:
      "A Government of Karnataka scheme for turning student ideas into ventures. Teams of students, research scholars or alumni take a regional problem to proposal; fifteen are shortlisted each year and up to ten funded, each with seed money of up to ₹3 lakh, an assigned mentor and quarterly review.",
    meta: "1st floor, IS&E department",
    body: [
      "NAIN is a scheme of the Department of IT, BT and S&T, Government of Karnataka, to promote innovation in educational institutions and support students in becoming entrepreneurs. BIET has established its NAIN centre on the first floor of the IS&E department.",
      "Teams identify regional and local problems and convert the idea into a project proposal. Proposals are reviewed by the institute's Project Monitoring and Assessment Group, which forwards fifteen potential projects to the state steering committee; up to ten are then funded. Each funded team is assigned a mentor, and the steering committee reviews progress quarterly.",
      "To keep students motivated the centre runs ideathons, hackathons and entrepreneurship boot camps on a regular basis.",
    ],
    facts: [
      { label: "Seed funding", value: "Up to ₹3 lakh per team" },
      { label: "Proposals shortlisted", value: "15 forwarded · up to 10 funded" },
      { label: "Eligible", value: "UG, PG, research scholars and alumni" },
      { label: "Located", value: "1st floor, IS&E department" },
    ],
    photos: NAIN_PHOTOS,
    source: "https://www.bietdvg.edu/campus_life/nain",
  },
  {
    slug: "edc",
    name: "Entrepreneurship Development Cell",
    abbr: "EDC",
    strap:
      "Constituted to build an entrepreneurial culture in the institute and inaugurated in May 2022. It runs awareness camps and development programmes, business-plan competitions with industry, and interaction sessions with founders and financiers — and guides aspiring founders through proposals, approvals and loans.",
    meta: "Dr. Sujith Kumar S H · Convener",
    body: [
      "The institute constituted the Entrepreneurship Development Cell to create an entrepreneurial culture among students and to foster techno-entrepreneurship for the generation of wealth and employment.",
      "The cell organises entrepreneurship awareness camps and development programmes, runs business-plan competitions in collaboration with industry, arranges interaction programmes with entrepreneurs and financial organisations, and guides aspiring founders in preparing project proposals and obtaining approvals and loans.",
    ],
    people: [
      { name: "Dr. Sujith Kumar S H", role: "Convener · Prof. & HOD, MBA" },
      { name: "Dr. G S Sunitha", role: "Co-convener · Prof. & HOD, E&CE" },
    ],
    photos: EDC_PHOTOS,
    source: "https://www.bietdvg.edu/edc",
  },
  {
    slug: "skill-development-centre",
    name: "Skill Development Centre",
    abbr: "SDC",
    strap:
      "An initiative under the National Education Policy, equipped with a five-axis machine, CNC turning, additive manufacturing and IoT hardware. Students and professionals train on CNC operation, robot programming, 3D modelling and automation, alongside communication and leadership.",
    meta: "Dr. Sharan A S · Co-ordinator",
    body: [
      "The Skill Development Centre, an initiative under the National Education Policy, provides hands-on training in current industrial technologies to students and professionals. It is equipped with a five-axis machine, CNC turning, additive manufacturing and Internet of Things hardware.",
      "Courses cover IoT for machines, additive manufacturing, and programming in milling and turning, alongside communication and leadership. Learners can operate CNC machines, program robots, design 3D models and build IoT systems for automation.",
    ],
    people: [{ name: "Dr. Sharan A S", role: "Co-ordinator · Associate Professor" }],
    photos: SKILL_CENTRE_PHOTOS,
    source: "https://www.bietdvg.edu/SDL",
  },
  {
    slug: "idea-lab",
    name: "AICTE IDEA Lab",
    abbr: "IDEA LAB",
    strap:
      "An AICTE-funded lab for prototyping and design thinking, being set up under the AICTE guidelines. Tenders for its equipment, tools and machines closed on 30 August 2025.",
    meta: "In procurement",
    body: [
      "The institute is setting up an AICTE IDEA Lab under the AICTE guidelines for promoting innovation, design thinking and prototyping skills among students and faculty. Tenders for the supply, installation and commissioning of its equipment, tools and machines closed on 30 August 2025.",
    ],
    photos: [],
    source: "https://www.bietdvg.edu/idea_lab",
  },
];

// ---------------------------------------------------------------------------
// Venues and amenities
// ---------------------------------------------------------------------------

export const CULTURAL_CENTRE = {
  name: "S. S. Mallikarjun Cultural Centre",
  short: "SSM Cultural Centre",
  // The source opens with a paragraph about rural students lacking cultural
  // facilities; dropped here — it says nothing about the building.
  intro: [
    "The auditorium is built in a contemporary architectural style, mixing postmodern and high-tech architecture with highly conceptual and expressive forms — sculpture on an enormous scale. Architectural designs were supplied and the work supervised by M/s Shankar Associates, Coimbatore.",
    "Modern green rooms with all facilities are provided separately for boys and girls, with a rest room for emergencies, a well-equipped cafeteria for performances, and ample parking with green landscaping. Many national leaders and well-known artists have visited the auditorium and appreciated the facilities.",
  ],
  facts: [
    { label: "Seating", value: "700, with two VIP balconies" },
    { label: "Built-up area", value: "≈5,500 sq m" },
    { label: "Cost", value: "≈₹28 crore" },
    { label: "Podium", value: "Made-to-order AHA-ELF 72D digital podium" },
  ],
  /** Named suppliers, which is unusually specific and worth keeping. */
  systems: [
    { what: "Projection", who: "Christie, Canada" },
    { what: "Audio", who: "BOSCH, US — 20 Hz to 20,000 Hz" },
    { what: "Acoustics", who: "HARA Design, Germany" },
    { what: "Lighting", who: "Made-to-order illumination rig" },
  ],
  photos: AUDITORIUM_PHOTOS,
  source: "https://www.bietdvg.edu/facilities/auditorium",
};

export interface Amenity {
  slug: string;
  name: string;
  /** Short line for a card. */
  strap: string;
  body: string[];
  /** Route on this site that carries the full story, where one exists. */
  to?: string;
  photos: Media[];
  source: string;
}

export const AMENITIES: Amenity[] = [
  {
    slug: "library",
    name: "Central Library",
    strap:
      "Three floors and two mezzanines of the Administrative Block, with open-access stacks, an OPAC, a digital library and Wi-Fi.",
    body: [
      "The Library & Information Centre occupies three floors of the Administrative Block, plus two mezzanines, over 2,661 sq m. Nine staff including a qualified librarian run it, with seating for 600 readers.",
      "Housekeeping is automated on the EasyLib package; the library runs open access with an Online Public Access Catalogue. It subscribes to 139 print journals and to e-journals through the VTU Consortium, and holds nearly 4,485 back volumes. Every engineering department also keeps its own departmental library.",
    ],
    to: ROUTES.library,
    photos: LIBRARY_PHOTOS,
    source: "https://www.bietdvg.edu/library",
  },
  {
    slug: "hostels",
    name: "Hostels",
    strap:
      "Separate boys' and ladies' hostels, each with its own dining halls, reading rooms and indoor games hall.",
    body: [
      "The boys' hostel was founded in 1984 on 4.5 acres and is known for its discipline; the Amrutha ladies' hostel followed in 2008. Both run two dining halls apiece, North and South Indian, managed by the hostel administration itself.",
      "Indoor games halls, reading rooms, solar hot water, RO drinking water, CCTV coverage and captive power are provided across the blocks; ragging is prohibited outright.",
    ],
    to: ROUTES.hostels,
    photos: HOSTELS.flatMap((h) => h.photos ?? []),
    source: "https://www.bietdvg.edu/facilities/boys_hostel",
  },
  {
    slug: "canteen",
    name: "Canteen & bakery",
    strap:
      "A modern kitchen where food is steam cooked and crockery is sterilised, with a bakery shop alongside.",
    body: [
      "The college canteen is designed to cater to 1,000 students and staff — a place where students congregate not only to eat but to exchange views. It has a modern kitchen where food is steam cooked, and washed dishes are sterilised for hygienic serving. There is also a bakery shop on campus.",
    ],
    photos: CANTEEN_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/canteen",
  },
  {
    slug: "health",
    name: "Health centre",
    strap:
      "An institute health centre, with referral to the Bapuji and SS hospitals and two dental colleges.",
    body: [
      "The institute runs its own health centre, with recourse to Bapuji Hospital, SS Hospital, Bapuji Dental College & Hospital and the College of Dental Sciences.",
      "All department laboratories carry first-aid kits, and the Biomedical Engineering department has the equipment to measure blood pressure, body temperature, heart rate, oxygen saturation and ECG, and to conduct eye and ear testing.",
    ],
    photos: HEALTH_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/dispensary",
  },
  {
    slug: "gym",
    name: "Multi gym",
    strap:
      "Managed by the Students' Academic Council, with a second gym and the “EKALAVYA” games hall in the boys' hostel.",
    body: [
      "A multi gym on campus, managed by the Students' Academic Council. The boys' hostel keeps a second multi gym of its own for physical fitness, alongside the “EKALAVYA” indoor games hall.",
    ],
    photos: GYM_PHOTOS,
    source: "https://www.bietdvg.edu/campus_life/gym",
  },
  {
    slug: "guest-house",
    name: "Silver-Line Guest House",
    strap: "On-campus accommodation for visiting guests and parents.",
    body: ["The institute's Silver-Line Guest House accommodates visiting guests on campus."],
    photos: GUEST_HOUSE_PHOTOS,
    source: "https://www.bietdvg.edu/facilities/guest_house",
  },
];

// ---------------------------------------------------------------------------
// Sport
// ---------------------------------------------------------------------------

/**
 * The source's sports page publishes annual reports and a gallery, and names no
 * disciplines in prose. The disciplines named in `intro` are the ones its own
 * photographs are captioned for — evidence, not a claim of the full roster.
 */
export const SPORTS = {
  intro: [
    "The institute fields teams at VTU inter-collegiate level and publishes an annual sports report each year. Its own photographic record covers athletics, badminton, basketball, kabaddi, throwball and football, for men's and women's teams alike.",
  ],
  /** Report years the source lists, newest first. */
  reportYears: ["2023-24", "2022-23", "2021-22", "2019-20", "2018-19", "2017-18"],
  photos: SPORTS_PHOTOS,
  source: "https://www.bietdvg.edu/facilities/sport",
};

// ---------------------------------------------------------------------------
// Publications and ceremony
// ---------------------------------------------------------------------------

export interface Publication {
  name: string;
  strap: string;
  detail: string;
}

export const PUBLICATIONS: Publication[] = [
  {
    name: "Nirmana",
    strap: "Annual technical magazine",
    detail: "Currently in its seventh edition, published as Nirmana 6.0.",
  },
  {
    name: "BIETECH VOICE",
    strap: "Institute magazine",
    detail: "The institute's own periodical, published alongside Technowave.",
  },
  {
    name: "TECHNOWAVE",
    strap: "Technical annual",
    detail: "Editions published for 2021-23 and 2023-24.",
  },
];

export interface Graduation {
  ordinal: string;
  date: string;
  /**
   * The "View More" target the source publishes for each ceremony. Every one is
   * a Google Photos album on the institute's own account — there is no gallery
   * on its site to ingest, so these are linked out rather than mirrored.
   */
  album?: string;
}

/** Graduation ceremonies the source lists, newest first. */
export const GRADUATIONS: Graduation[] = [
  {
    ordinal: "41st Graduation Day",
    date: "21 October 2023",
    album: "https://photos.app.goo.gl/bgLbzxYJAS9VkT9b8",
  },
  {
    ordinal: "40th Graduation Day",
    date: "10 June 2023",
    album: "https://photos.app.goo.gl/UTer2Gq9iZCioFu48",
  },
  {
    ordinal: "39th Graduation Day (2021)",
    date: "7 May 2022",
    album: "https://photos.app.goo.gl/RKgHJot2Cgux6Wtc8",
  },
  {
    ordinal: "Graduands' Day 2019",
    date: "8 June 2019",
    album: "https://photos.app.goo.gl/bZ7owD6evNDMB8yT6",
  },
];

export const GRADUATION_SOURCE = "https://www.bietdvg.edu/campus_life/graduation_day";

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

/**
 * A walk across campus, told with the institute's own photographs.
 *
 * Captions describe what is actually in the frame. The page this replaces ran
 * an invented time-of-day narrative ("6:42 AM · First light at the gate") over
 * empty placeholders; nothing in it was sourced, so none of it is kept.
 */
export interface GallerySlide {
  media: Media;
  title: string;
  caption: string;
}

export const GALLERY: GallerySlide[] = [
  {
    media: shot(AUDITORIUM_PHOTOS, "auditorium-12.jpg"),
    title: "Inside the hall",
    caption:
      "The acoustic ceiling and lighting rig, seen from the stage, with the balcony behind. Green rooms for boys and girls sit off the wings.",
  },
  {
    media: shot(SPORTS_PHOTOS, "college-kabaddi-team.jpg"),
    title: "The kabaddi team",
    caption: "One of the sides the institute fields at VTU inter-collegiate level.",
  },
  {
    media: shot(SPORTS_PHOTOS, "college-baskete-ball-team-girls.jpg"),
    title: "The women's basketball team",
    caption:
      "The institute's photographic record covers men's and women's sides in badminton, basketball and athletics alike.",
  },
  {
    media: shot(SPORTS_PHOTOS, "perform-in-athletic.jpg"),
    title: "Athletics",
    caption: "Competing at VTU level. An annual sports report has been published each year since 2017-18.",
  },
  {
    media: shot(NCC_PHOTOS, "ncc-03.jpg"),
    title: "NCC cadets",
    caption: "“Unity and Discipline” — the corps trains under Associate NCC Officer Lt. Anjaneya L.H.",
  },
  {
    media: shot(CANTEEN_PHOTOS, "canteen-01.jpg"),
    title: "The canteen",
    caption:
      "Garlanded for its opening. Designed for a thousand students and staff, with a steam kitchen, sterilised crockery and a bakery shop.",
  },
  {
    media: shot(GYM_PHOTOS, "gym-01.jpg"),
    title: "The multi gym",
    caption: "Run by the students' council itself, alongside a second gym in the boys' hostel.",
  },
  {
    media: shot(WATER_PHOTOS, "ro-unit.jpg"),
    title: "The reverse-osmosis plant",
    caption:
      "Two 1,000-litre-an-hour units — sand filter, carbon filter, RO membrane — serving around 3,500 students, and doubling as teaching rigs.",
  },
];

/** Hero image: the cultural centre, the most recognisable building on campus. */
export const CAMPUS_LIFE_HERO: Media = shot(AUDITORIUM_PHOTOS, "auditorium-19.jpg");

/**
 * Every photograph already spent on the hero or the closing gallery.
 *
 * The page has 93 images to draw on, so no frame needs to appear twice. Section
 * galleries filter against this set with `unspent()` rather than being hand-tuned
 * — otherwise adding a gallery slide silently duplicates whatever section already
 * showed it.
 */
const SPENT = new Set<string>([
  CAMPUS_LIFE_HERO.url,
  ...GALLERY.map((s) => s.media.url),
  ...SERVICE_WINGS.map((w) => w.cardPhoto.url),
  DAVANA.photo.url,
]);

/** The photos in `pool` that the hero and gallery have not already used. */
export function unspent(pool: Media[]): Media[] {
  return pool.filter((m) => !SPENT.has(m.url));
}
